import React, { useState } from 'react';
import { useStudy } from '../context/StudyContext';
import './StudySession.css';

function StudySession({ session, studySetId, onBack, onComplete, onReviewMistakes, onCategoryProgressUpdate, onModeCompletion }) {
  const { checkAnswer } = useStudy();
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [isComplete, setIsComplete] = useState(false);

  const currentQuestion = session.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / session.questions.length) * 100;

  const handleCheckAnswer = (answer) => {
    const result = checkAnswer(studySetId, currentQuestion.id, answer);

    setFeedback(result);

    if (result.correct) {
      setScore(score + 1);
    }

    setAnsweredQuestions([...answeredQuestions, {
      question: currentQuestion.question,
      userAnswer: answer,
      correct: result.correct,
      correctAnswer: result.correctAnswer,
      forcedCorrect: false,
      category: currentQuestion.category || 'Uncategorized'
    }]);
  };

  const handleForceCorrect = () => {
    // Update score
    setScore(score + 1);
    
    // Update the last answered question to mark it as correct
    const updatedAnswers = [...answeredQuestions];
    updatedAnswers[updatedAnswers.length - 1] = {
      ...updatedAnswers[updatedAnswers.length - 1],
      correct: true,
      forcedCorrect: true
    };
    setAnsweredQuestions(updatedAnswers);
    
    // Update feedback to show as correct
    setFeedback({
      ...feedback,
      correct: true
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (session.mode === 'multiple-choice' && selectedChoice !== null) {
      handleCheckAnswer(currentQuestion.choices[selectedChoice]);
    } else if (session.mode !== 'multiple-choice' && userAnswer.trim()) {
      handleCheckAnswer(userAnswer);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < session.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setUserAnswer('');
      setSelectedChoice(null);
      setShowHint(false);
      setFeedback(null);
    } else {
      setIsComplete(true);
      // Calculate category progress using stored category from each answered question
      if (onCategoryProgressUpdate) {
        const categoryResults = {};
        answeredQuestions.forEach((q) => {
          const category = q.category;
          if (!categoryResults[category]) {
            categoryResults[category] = { attempted: 0, correct: 0 };
          }
          categoryResults[category].attempted++;
          if (q.correct) {
            categoryResults[category].correct++;
          }
        });
        onCategoryProgressUpdate(categoryResults);
      }
      // Track mode completion if 100%
      if (onModeCompletion) {
        const percentage = Math.round((score / session.questions.length) * 100);
        const categoryResults = {};
        answeredQuestions.forEach((q) => {
          const category = q.category;
          if (!categoryResults[category]) {
            categoryResults[category] = { attempted: 0, correct: 0 };
          }
          categoryResults[category].attempted++;
          if (q.correct) {
            categoryResults[category].correct++;
          }
        });
        onModeCompletion(session.mode, percentage, categoryResults);
      }
    }
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setUserAnswer('');
    setSelectedChoice(null);
    setShowHint(false);
    setFeedback(null);
    setScore(0);
    setAnsweredQuestions([]);
    setIsComplete(false);
  };

  const handleReviewMistakes = () => {
    // Get IDs of questions that were answered incorrectly
    const incorrectQuestionIds = answeredQuestions
      .filter(q => !q.correct)
      .map(q => {
        // Find the original question ID from the session
        const originalQuestion = session.questions.find(sq => sq.question === q.question);
        return originalQuestion?.id;
      })
      .filter(Boolean);

    // Pass the incorrect question IDs back to App to create a review session
    onReviewMistakes(incorrectQuestionIds);
  };

  if (isComplete) {
    const percentage = Math.round((score / session.questions.length) * 100);
    return (
      <div className="study-session complete">
        <div className="completion-card">
          <div className="completion-icon">
            {percentage >= 80 ? '🎉' : percentage >= 60 ? '👍' : '📚'}
          </div>
          <h2>Session Complete!</h2>
          <div className="final-score">
            <div className="score-circle">
              <span className="score-number">{percentage}%</span>
            </div>
            <p className="score-text">
              You got {score} out of {session.questions.length} correct!
            </p>
          </div>

          {percentage >= 80 && (
            <p className="encouragement excellent">Excellent work! You've mastered this material! 🌟</p>
          )}
          {percentage >= 60 && percentage < 80 && (
            <p className="encouragement good">Good job! Keep practicing to improve! 💪</p>
          )}
          {percentage < 60 && (
            <p className="encouragement needs-work">Keep studying! Practice makes perfect! 📖</p>
          )}

          <div className="review-section">
            <h3>Review Your Answers</h3>
            <div className="answers-review">
              {answeredQuestions.map((q, idx) => (
                <div key={idx} className={`review-item ${q.correct ? 'correct' : 'incorrect'}`}>
                  <div className="review-header">
                    <span className="review-icon">{q.correct ? '✓' : '✗'}</span>
                    <span className="review-number">Question {idx + 1}</span>
                    {q.forcedCorrect && (
                      <span className="forced-badge">Manual Override</span>
                    )}
                  </div>
                  <p className="review-question">{q.question}</p>
                  <p className="review-your-answer">
                    <strong>Your answer:</strong> {q.userAnswer}
                  </p>
                  {!q.correct && (
                    <p className="review-correct-answer">
                      <strong>Correct answer:</strong> {Array.isArray(q.correctAnswer) ? q.correctAnswer[0] : q.correctAnswer}
                    </p>
                  )}
                  {q.forcedCorrect && (
                    <p className="forced-note">
                      You marked this as correct (main idea was right)
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="completion-actions">
            <button className="action-button primary" onClick={handleRestart}>
              Try Again
            </button>
            {answeredQuestions.filter(q => !q.correct).length > 0 && (
              <button className="action-button review" onClick={handleReviewMistakes}>
                Review Mistakes ({answeredQuestions.filter(q => !q.correct).length})
              </button>
            )}
            <button className="action-button secondary" onClick={onComplete}>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="study-session">
      <div className="session-header">
        <button className="back-button-small" onClick={onBack}>
          ← Back
        </button>
        <div className="session-info">
          <span className="mode-badge">{session.mode}</span>
          <span className="score-display">Score: {score}/{currentQuestionIndex}</span>
        </div>
      </div>

      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
      </div>

      <div className="question-card">
        <div className="question-header">
          <span className="question-number">
            Question {currentQuestionIndex + 1} of {session.questions.length}
          </span>
          {currentQuestion.difficulty && (
            <span className={`difficulty-tag ${currentQuestion.difficulty}`}>
              {currentQuestion.difficulty}
            </span>
          )}
        </div>

        <h3 className="question-text">{currentQuestion.question}</h3>

        {session.mode === 'beginner' && currentQuestion.hint && (
          <div className="hint-section">
            {!showHint ? (
              <button className="hint-button" onClick={() => setShowHint(true)}>
                💡 Show Hint
              </button>
            ) : (
              <div className="hint-box">
                <strong>Hint:</strong> {currentQuestion.hint}
              </div>
            )}
          </div>
        )}

        {!feedback ? (
          <form onSubmit={handleSubmit} className="answer-form">
            {session.mode === 'multiple-choice' ? (
              <div className="choices">
                {currentQuestion.choices && currentQuestion.choices.map((choice, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`choice-button ${selectedChoice === idx ? 'selected' : ''}`}
                    onClick={() => setSelectedChoice(idx)}
                  >
                    <span className="choice-letter">{String.fromCharCode(65 + idx)}</span>
                    <span className="choice-text">{choice}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-input-group">
                <input
                  type="text"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  placeholder="Type your answer here..."
                  className="answer-input"
                  autoFocus
                />
              </div>
            )}

            <button
              type="submit"
              className="submit-button"
              disabled={session.mode === 'multiple-choice' ? selectedChoice === null : !userAnswer.trim()}
            >
              Submit Answer
            </button>
          </form>
        ) : (
          <div className={`feedback ${feedback.correct ? 'correct' : 'incorrect'}`}>
            <div className="feedback-icon">
              {feedback.correct ? '✓' : '✗'}
            </div>
            <h4>{feedback.correct ? 'Correct!' : 'Not quite right'}</h4>

            {!feedback.correct && (
              <>
                <p className="correct-answer">
                  The correct answer is: <strong>{Array.isArray(feedback.correctAnswer) ? feedback.correctAnswer[0] : feedback.correctAnswer}</strong>
                </p>
                {(session.mode === 'free-response' || session.mode === 'beginner') && (
                  <button className="force-correct-button" onClick={handleForceCorrect}>
                    ✓ Mark as Correct (Main idea is right)
                  </button>
                )}
              </>
            )}
            {feedback.explanation && (
              <div className="explanation">
                <p>{feedback.explanation}</p>
              </div>
            )}

            <button className="next-button" onClick={handleNext}>
              {currentQuestionIndex < session.questions.length - 1 ? 'Next Question →' : 'Complete Session'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudySession;
